'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { cn } from '@utils';
import { Button } from 'react-aria-components';

import { Close, Menu } from '../icons';
import { Drawer, DrawerContent, DrawerHeader } from '../ui/Drawer';
import { isNavigationItemActive } from './_utils';
import { HeaderNavButton } from './HeaderNavButton';
import { CTAItem, NavigationItem } from './types';
import { useMenuItems } from './useMenuItems';

export const MobileNav = () => {
	const drawerDirection = 'right';
	const pathname = usePathname();
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const { menuItems, callToActionItems } = useMenuItems();

	const NavigationItem = ({
		item,
		active,
		onClick,
	}: {
		item: NavigationItem;
		active?: boolean;
		onClick?: () => void;
	}) => {
		return (
			<Link
				href={item.href}
				className="group relative flex h-12 flex-row md:h-16"
				onClick={onClick}
			>
				<div
					className={cn(
						'bg-light-design-accent absolute top-0 left-0 z-40 h-full w-2',
						'group-hover:bg-light-action-primary-hover',
						active && 'bg-neutral-lightest',
					)}
				/>
				<div
					className={cn(
						'decoration-light-link border-neutral-lighter border-t-1 decoration-1 underline-offset-2',
						'text-light-text bg-neutral-lightest relative flex h-full w-full cursor-pointer items-center pr-4 pl-6',
						'group-hover:text-light-link group-hover:bg-light-background-alt group-hover:underline',
					)}
				>
					{item.icon ? (
						<div className="flex items-center justify-center gap-3">
							{item.icon(active)}
							<p
								className={cn(
									'group-hover:text-light-link truncate text-left text-xs md:text-sm',
									active && 'text-light-heading font-semibold',
								)}
							>
								{item.iconLabel}
							</p>
						</div>
					) : (
						<p
							className={cn(
								'group-hover:text-light-link truncate text-left text-xs md:text-sm',
								active && 'text-light-heading font-semibold',
							)}
						>
							{item.label}
						</p>
					)}
				</div>
			</Link>
		);
	};

	const CTAButton = ({ item }: { item: CTAItem }) => (
		<Button
			onPress={() => {
				item.onClick();
				setIsMenuOpen(false);
			}}
			className={cn(
				'group border-t-dark-border cursor-pointer border-t-1 hover:brightness-115',
				'bg-dark-background flex h-12 w-full flex-col items-start justify-center px-4 md:h-16',
			)}
		>
			<div className="flex flex-row items-center gap-3">
				{item.icon}
				<p className="text-dark-text truncate text-left text-xs decoration-1 underline-offset-2 group-hover:underline md:text-sm">
					{item.label}
				</p>
			</div>
		</Button>
	);

	return (
		<Drawer direction={drawerDirection} open={isMenuOpen} onOpenChange={setIsMenuOpen} handleOnly>
			<HeaderNavButton
				label="Menu"
				onPress={() => setIsMenuOpen(true)}
				icon={<Menu className="text-dark-action-secondary size-icon-sm md:size-icon-lg" />}
			/>
			<DrawerContent placement={drawerDirection} className="bg-dark-background-shade">
				<div className="flex h-screen flex-col overflow-x-hidden overflow-y-auto">
					{/* Drawer header containing menu title and close button */}
					<DrawerHeader className="bg-dark-background flex flex-row items-center justify-between">
						<p className="text-dark-text px-4 text-xs md:text-sm">Menu</p>
						<HeaderNavButton
							autoFocus
							label="Close"
							onPress={() => setIsMenuOpen(false)}
							icon={<Close className="text-dark-action-secondary size-icon-sm md:size-icon-lg" />}
						/>
					</DrawerHeader>
					{/* Main navigation links that route within the application */}
					{menuItems.map(item => (
						<NavigationItem
							key={item.href}
							item={item}
							active={isNavigationItemActive(item, pathname)}
							onClick={() => setIsMenuOpen(false)}
						/>
					))}
					{/* Secondary action buttons like logout and portal switching */}
					{callToActionItems.map(
						(item, index) => item.visible && <CTAButton key={index} item={item} />,
					)}
				</div>
			</DrawerContent>
		</Drawer>
	);
};
